import React, { useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import BaseChart, { useChartInstance } from '@src/components/common/BaseChart';
import { formatMetricDataForChart } from '../../utils/format';
import { getMetricDisplayName, getMetricUnitByKey } from '@src/utils/metrics';
import humanizeUtil from '@utils/humanizeUtil';
import { filterNAMetricsAtom } from '@src/atoms/page';
import { CardWrapper, StyledStatistic } from './styled';
import { EllipsisTooltip } from '@src/components/common';
import useSelectMetric from '../../hooks/useSelectMetric';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms/index';
import { DeletedMetricWithBorder } from '@components/common/DeletedMetric';
import { percentileConfig } from '@src/utils/metrics/constants';

function MetricTrend({ wid, data, metricKey, total, style, percentile = null }) {
  const errorData = useAtomValue(errorDataAtom);
  const { instance, chartInstanceRefCb } = useChartInstance({});
  const metricsToNA = useAtomValue(filterNAMetricsAtom);
  const chartOption = useMemo(
    () => formatMetricDataForChart({ chartData: data, metricKey }),
    [data, metricKey],
  );

  const naMode = metricsToNA.includes(metricKey);

  useEffect(() => {
    if (instance) {
      instance.setOption(chartOption);
    }
  }, [instance, chartOption]);

  const selectMetric = useSelectMetric();

  const handleClick = () => {
    selectMetric(metricKey);
  };

  const unit = getMetricUnitByKey({ metricKey });
  const { humanizedNumber, humanizedUnit } = humanizeUtil(total, metricKey, unit);

  // Get percentile label if applicable
  const percentileLabel = useMemo(() => {
    if (percentile && percentile !== 'avg') {
      const config = percentileConfig[percentile as keyof typeof percentileConfig];
      if (config) {
        return config.short;
      }
    }
    return null;
  }, [percentile]);

  return errorData[metricKey] ? (
    <div style={{ minHeight: 100 }}>
      <DeletedMetricWithBorder />
    </div>
  ) : (
    <CardWrapper style={style} onClick={handleClick} className={naMode ? `na-mode` : ``}>
      <StyledStatistic
        title={<EllipsisTooltip text={getMetricDisplayName({ metricKey })} />}
        value={naMode ? 'Not Applicable' : `${humanizedNumber} ${humanizedUnit}`}
        suffix={
          naMode ? null : (
            <span>
              {percentileLabel && (
                <span style={{ marginLeft: 4, fontSize: '0.85em', opacity: 0.8 }}>
                  ({percentileLabel})
                </span>
              )}
            </span>
          )
        }
      />
      <BaseChart chartInstanceRefCb={chartInstanceRefCb} style={{ height: 60, width: '100%' }} />
    </CardWrapper>
  );
}

export default MetricTrend;
